import { Box, Group, Text } from '@ekhodealer/ekho-common/rev';

import type { FicoRating } from '@ekhodealer/ekho-common';

const FICO_RATING_COLORS: Record<FicoRating, string> = {
	Exceptional: '#00805E',
	'Very Good': '#00805E',
	Good: '#479994',
	Fair: '#D97F18',
	Poor: '#D32733',
};

/**
 * Derives a FicoRating from a numeric FICO score (300-850).
 * Mirrors the backend `getFicoRating` logic.
 */
const getFicoRating = (score: number): FicoRating => {
	if (score >= 800) return 'Exceptional';
	if (score >= 740) return 'Very Good';
	if (score >= 670) return 'Good';
	if (score >= 580) return 'Fair';
	return 'Poor';
};

interface FicoScoreDisplayProps {
	/** Numeric FICO score (300-850) */
	ficoScore: number;
	/** Optional pre-computed rating string. If not provided, derived from ficoScore. */
	ficoScoreRating?: string;
}

/**
 * Displays a FICO score with a colored indicator dot and rating label.
 * Reusable across leads and orders drawers for consistent styling.
 */
export const FicoScoreDisplay = ({ ficoScore, ficoScoreRating }: FicoScoreDisplayProps) => {
	const rating = (ficoScoreRating as FicoRating) ?? getFicoRating(ficoScore);
	const color = FICO_RATING_COLORS[rating] ?? FICO_RATING_COLORS[getFicoRating(ficoScore)];

	return (
		<Group itemsSpacing='8' itemsAlignY='center' noWrap>
			<Box
				rounding='8'
				customBackground={color}
				children={<div style={{ width: 12, height: 12 }} />}
			/>
			<Text size='bodySmall' weight='semibold'>
				{ficoScore} ({rating})
			</Text>
		</Group>
	);
};
